import { ChangeDetectionStrategy, Component, HostBinding, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ContactService } from '@fi-shared/fi-contacts/contact.service';
import { FiAddressTypes } from '../../core/address-types';
import { AddressModel } from '../address/address.model';
import { AppTextService } from '@fi-shared/app-text';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import { FiComponentsContainer } from '@fi-shared/fi-components-container';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';

interface IAddressContact {
    Id: FiAddressTypes;
    Name: string;
}

@Component({
    selector: 'fi-addresses',
    templateUrl: './addresses.component.html',
    styleUrls: ['./addresses.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-addresses' },
    providers: [{ provide: FiComponentsContainer, useExisting: AddressesComponent }],
    standalone: false,
})
export class AddressesComponent extends FiComponentsContainer implements OnInit {
    @Input() addresses: AddressModel[];
    @Input() isRequired: boolean;
    @Input() isNewAddButtonLayout: boolean;
    @HostBinding('class.mobile-layout') get isMobileSmall(): boolean {
        return this.detectDevicesService.isMobileSmall();
    }
    addressContactTypes: IAddressContact[] = [];
    private readonly useSimpleName: boolean = true;
    readonly contactTypeName: string = 'AddressType';

    contactData = this.fiContactsService.getContactData(() => this.addresses);

    constructor(
        private ccmContactService: ContactService,
        private appTextService: AppTextService,
        private detectDevicesService: DetectDevicesService,
        private fiContactsService: FiContactsService,
    ) {
        super();
    }

    ngOnInit() {
        this.buildViewAddresses();
        this.setAddressContactType();
    }

    private setAddressContactType() {
        this.addressContactTypes = this.ccmContactService.getAddressTypes().map((addressType: FiAddressTypes) => {
            return {
                Id: addressType,
                Name: this.getAddressContactTypeName(addressType),
            };
        });
    }

    private getAddressContactTypeName(addressType: FiAddressTypes): string {
        switch (addressType) {
            case FiAddressTypes.HomeAddress:
                return this.appTextService.get('common.home');
            case FiAddressTypes.WorkAddress:
                return this.appTextService.get('common.work');
            case FiAddressTypes.OtherAddress:
                return this.appTextService.get('common.other');
            default:
                return this.ccmContactService.getAddressTypeName(addressType, this.useSimpleName);
        }
    }

    buildViewAddresses() {
        this.addresses.sort((address: AddressModel) => (address.IsDefault === true ? -1 : 0));
    }

    onDefaultSet(defaultAddress) {
        this.addresses.forEach((address: AddressModel) => {
            address.IsDefault = address === defaultAddress;
        });
        this.buildViewAddresses();
    }

    isDefaultAddress(address): boolean {
        return this.addresses.length === 1 || address.IsDefault;
    }

    addNewAddress() {
        const addressToAdd: AddressModel = new AddressModel();
        addressToAdd.AddressType = this.getNextAddressType();
        this.addresses.push(addressToAdd);
    }

    private getNextAddressType(): FiAddressTypes {
        if (!this.isAddressTypeChosen(FiAddressTypes.HomeAddress)) return FiAddressTypes.HomeAddress;

        if (!this.isAddressTypeChosen(FiAddressTypes.WorkAddress)) return FiAddressTypes.WorkAddress;

        return FiAddressTypes.OtherAddress;
    }

    private isAddressTypeChosen(addressType: FiAddressTypes): boolean {
        return this.addresses.some((address: AddressModel) => address.AddressType === addressType);
    }

    removeAddress(address: AddressModel) {
        this.addresses.splice(this.addresses.indexOf(address), 1);
        this.buildViewAddresses();
    }

    isEditDeleteDisabled(address: AddressModel): boolean {
        return this.ccmContactService.isAddressImported(address.AddressType);
    }
}
